import { api } from './client';
import type {
  AllergenDto,
  CategoryDto,
  ClientDto,
  DishDetail,
  DishSummary,
  EquipmentDto,
  IngredientDetail,
  IngredientSummary,
  LoginResponse,
  ManagedUser,
  MenuDetail,
  MenuSummary,
  MenuSummaryCalc,
  MenuTypeDto,
  OrderDetail,
  OrderSummary,
  PageResponse,
  PriceDto,
  ProcurementListDto,
  QuoteDto,
  RecipeCost,
  SaveUserRequest,
  SettingDto,
  SupplierDto,
  UnitDto,
  UsageDto,
  UserProfile,
  VersionDetail,
} from '@/types/api';

/** Strips undefined values so axios does not serialise them as "undefined". */
const clean = (params: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  );

export const authApi = {
  login: (username: string, password: string) =>
    api.post<LoginResponse>('/auth/login', { username, password }).then((r) => r.data),
  refresh: () => api.post<LoginResponse>('/auth/refresh').then((r) => r.data),
  logout: () => api.post<void>('/auth/logout').then((r) => r.data),
  me: () => api.get<UserProfile>('/me').then((r) => r.data),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<void>('/auth/change-password', { currentPassword, newPassword }).then((r) => r.data),
};

export const userAdminApi = {
  list: (search?: string, page = 0, size = 25) =>
    api
      .get<PageResponse<ManagedUser>>('/admin/users', { params: clean({ search, page, size }) })
      .then((r) => r.data),
  create: (body: SaveUserRequest) =>
    api.post<ManagedUser>('/admin/users', body).then((r) => r.data),
  update: (id: number, body: SaveUserRequest) =>
    api.put<ManagedUser>(`/admin/users/${id}`, body).then((r) => r.data),
  // The administrator does not know the current password, so only the new one is sent.
  resetPassword: (id: number, newPassword: string) =>
    api.post<void>(`/admin/users/${id}/password`, { newPassword }).then((r) => r.data),
  remove: (id: number) => api.delete<void>(`/admin/users/${id}`).then((r) => r.data),
};

export const referenceApi = {
  units: () => api.get<UnitDto[]>('/reference/units').then((r) => r.data),
  ingredientCategories: () =>
    api.get<CategoryDto[]>('/reference/ingredient-categories').then((r) => r.data),
  dishCategories: () => api.get<CategoryDto[]>('/reference/dish-categories').then((r) => r.data),
  allergens: () => api.get<AllergenDto[]>('/reference/allergens').then((r) => r.data),
  suppliers: () => api.get<SupplierDto[]>('/reference/suppliers').then((r) => r.data),
  equipment: () => api.get<EquipmentDto[]>('/reference/equipment').then((r) => r.data),
  menuTypes: () => api.get<MenuTypeDto[]>('/reference/menu-types').then((r) => r.data),
  settings: () => api.get<SettingDto[]>('/settings').then((r) => r.data),
  saveSettings: (values: Record<string, string>) =>
    api.put<Record<string, string>>('/settings', { values }).then((r) => r.data),
};

export interface IngredientQuery {
  [key: string]: unknown;
  search?: string;
  categoryId?: number;
  allergenId?: number;
  active?: boolean;
  page?: number;
  size?: number;
  sort?: string;
}

export const ingredientApi = {
  list: (query: IngredientQuery) =>
    api
      .get<PageResponse<IngredientSummary>>('/ingredients', { params: clean(query) })
      .then((r) => r.data),
  get: (id: number) => api.get<IngredientDetail>(`/ingredients/${id}`).then((r) => r.data),
  create: (body: unknown) =>
    api.post<IngredientDetail>('/ingredients', body).then((r) => r.data),
  update: (id: number, body: unknown) =>
    api.put<IngredientDetail>(`/ingredients/${id}`, body).then((r) => r.data),
  remove: (id: number) => api.delete<void>(`/ingredients/${id}`).then((r) => r.data),
  prices: (id: number) => api.get<PriceDto[]>(`/ingredients/${id}/prices`).then((r) => r.data),
  addPrice: (id: number, body: unknown) =>
    api.post<PriceDto>(`/ingredients/${id}/prices`, body).then((r) => r.data),
  deletePrice: (id: number, priceId: number) =>
    api.delete<void>(`/ingredients/${id}/prices/${priceId}`).then((r) => r.data),
  saveWasteNorm: (id: number, body: unknown) =>
    api.post(`/ingredients/${id}/waste-norms`, body).then((r) => r.data),
  usage: (id: number, newPrice?: string, asOf?: string) =>
    api
      .get<UsageDto[]>(`/ingredients/${id}/usage`, { params: clean({ newPrice, asOf }) })
      .then((r) => r.data),
};

export interface DishQuery {
  [key: string]: unknown;
  search?: string;
  categoryId?: number;
  dishType?: string;
  status?: string;
  asOf?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export const dishApi = {
  list: (query: DishQuery) =>
    api.get<PageResponse<DishSummary>>('/dishes', { params: clean(query) }).then((r) => r.data),
  get: (id: number) => api.get<DishDetail>(`/dishes/${id}`).then((r) => r.data),
  create: (body: unknown) => api.post<DishDetail>('/dishes', body).then((r) => r.data),
  update: (id: number, body: unknown) =>
    api.put<DishDetail>(`/dishes/${id}`, body).then((r) => r.data),
  remove: (id: number) => api.delete<void>(`/dishes/${id}`).then((r) => r.data),

  version: (dishId: number, versionId: number) =>
    api.get<VersionDetail>(`/dishes/${dishId}/versions/${versionId}`).then((r) => r.data),
  createVersion: (dishId: number, body?: unknown) =>
    api.post<VersionDetail>(`/dishes/${dishId}/versions`, body ?? {}).then((r) => r.data),
  updateVersion: (dishId: number, versionId: number, body: unknown) =>
    api.put<VersionDetail>(`/dishes/${dishId}/versions/${versionId}`, body).then((r) => r.data),
  activateVersion: (dishId: number, versionId: number) =>
    api
      .post<VersionDetail>(`/dishes/${dishId}/versions/${versionId}/activate`)
      .then((r) => r.data),
  scaleVersion: (dishId: number, versionId: number, targetPortions: string) =>
    api
      .post<VersionDetail>(`/dishes/${dishId}/versions/${versionId}/scale`, { targetPortions })
      .then((r) => r.data),
  diff: (dishId: number, left: number, right: number, asOf?: string) =>
    api
      .get(`/dishes/${dishId}/versions/diff`, { params: clean({ left, right, asOf }) })
      .then((r) => r.data),

  cost: (dishId: number, versionId: number, params: Record<string, unknown>) =>
    api
      .get<RecipeCost>(`/dishes/${dishId}/versions/${versionId}/cost`, { params: clean(params) })
      .then((r) => r.data),

  ttkPdfUrl: (dishId: number, versionId: number) =>
    `/dishes/${dishId}/versions/${versionId}/ttk.pdf`,
  calcCardPdfUrl: (dishId: number, versionId: number) =>
    `/dishes/${dishId}/versions/${versionId}/calc-card.pdf`,
};

export interface MenuQuery {
  [key: string]: unknown;
  search?: string;
  menuTypeId?: number;
  status?: string;
  template?: boolean;
  page?: number;
  size?: number;
  sort?: string;
}

export const menuApi = {
  list: (query: MenuQuery) =>
    api.get<PageResponse<MenuSummary>>('/menus', { params: clean(query) }).then((r) => r.data),
  get: (id: number, asOf?: string) =>
    api.get<MenuDetail>(`/menus/${id}`, { params: clean({ asOf }) }).then((r) => r.data),
  summary: (id: number, guests: number, asOf?: string, strategy?: string) =>
    api
      .get<MenuSummaryCalc>(`/menus/${id}/summary`, { params: clean({ guests, asOf, strategy }) })
      .then((r) => r.data),
  create: (body: unknown) => api.post<MenuDetail>('/menus', body).then((r) => r.data),
  update: (id: number, body: unknown) =>
    api.put<MenuDetail>(`/menus/${id}`, body).then((r) => r.data),
  clone: (id: number, body: unknown) =>
    api.post<MenuDetail>(`/menus/${id}/clone`, body).then((r) => r.data),
  refreshVersions: (id: number) =>
    api.post<MenuDetail>(`/menus/${id}/refresh-versions`).then((r) => r.data),
  remove: (id: number) => api.delete<void>(`/menus/${id}`).then((r) => r.data),
};

export interface OrderQuery {
  [key: string]: unknown;
  from?: string;
  to?: string;
  status?: string[];
  managerId?: number;
  clientId?: number;
  search?: string;
  page?: number;
  size?: number;
  sort?: string;
}

export const orderApi = {
  list: (query: OrderQuery) =>
    api.get<PageResponse<OrderSummary>>('/orders', { params: clean(query) }).then((r) => r.data),
  get: (id: number) => api.get<OrderDetail>(`/orders/${id}`).then((r) => r.data),
  create: (body: unknown) => api.post<OrderDetail>('/orders', body).then((r) => r.data),
  update: (id: number, body: unknown) =>
    api.put<OrderDetail>(`/orders/${id}`, body).then((r) => r.data),
  recalculate: (id: number, guests?: number, priceAsOf?: string) =>
    api
      .post<OrderDetail>(`/orders/${id}/recalculate`, clean({ guests, priceAsOf }))
      .then((r) => r.data),
  transition: (id: number, to: string, comment?: string) =>
    api.post<OrderDetail>(`/orders/${id}/transition`, { to, comment }).then((r) => r.data),

  quotes: (id: number) => api.get<QuoteDto[]>(`/orders/${id}/quotes`).then((r) => r.data),
  createQuote: (id: number, body?: unknown) =>
    api.post<QuoteDto>(`/orders/${id}/quotes`, body ?? {}).then((r) => r.data),
  quotePdfUrl: (orderId: number, quoteId: number) => `/orders/${orderId}/quotes/${quoteId}.pdf`,
};

export const clientApi = {
  list: (params: { search?: string; active?: boolean; page?: number; size?: number }) =>
    api.get<PageResponse<ClientDto>>('/clients', { params: clean(params) }).then((r) => r.data),
  get: (id: number) => api.get<ClientDto>(`/clients/${id}`).then((r) => r.data),
  create: (body: unknown) => api.post<ClientDto>('/clients', body).then((r) => r.data),
  update: (id: number, body: unknown) =>
    api.put<ClientDto>(`/clients/${id}`, body).then((r) => r.data),
  remove: (id: number) => api.delete<void>(`/clients/${id}`).then((r) => r.data),
};

export const procurementApi = {
  list: (params: { orderId?: number; page?: number; size?: number }) =>
    api
      .get<PageResponse<ProcurementListDto>>('/procurement', { params: clean(params) })
      .then((r) => r.data),
  get: (id: number) => api.get<ProcurementListDto>(`/procurement/${id}`).then((r) => r.data),
  generate: (body: unknown) =>
    api.post<ProcurementListDto>('/procurement/generate', body).then((r) => r.data),
  markLine: (id: number, body: unknown) =>
    api.put<ProcurementListDto>(`/procurement/${id}/lines`, body).then((r) => r.data),
  updateStatus: (id: number, status: string) =>
    api.put<ProcurementListDto>(`/procurement/${id}/status`, { status }).then((r) => r.data),
  xlsxUrl: (id: number) => `/procurement/${id}.xlsx`,
};

/**
 * Downloads a generated document through axios so the Authorization header is applied,
 * then hands the browser a blob. A plain link would omit the bearer token.
 */
export const downloadFile = async (path: string, filename: string) => {
  const response = await api.get(path, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
